/**
 * The permission catalogue lives in code, not in per-tenant seed rows: adding a permission is a
 * deploy, not a data backfill. Roles are tenant data and reference these keys.
 *
 * Key format: `<resource>.<action>`. Actions are verbs the backend enforces; the frontend only
 * uses them to hide what the user cannot do.
 */
export interface PermissionDef {
  group: PermissionGroup
  en: string
  ar: string
}

export type PermissionGroup =
  | 'administration'
  | 'crm'
  | 'sales'
  | 'pos'
  | 'purchasing'
  | 'catalog'
  | 'inventory'
  | 'accounting'
  | 'expenses'
  | 'workflows'
  | 'tasks'
  | 'service'
  | 'projects'
  | 'ai'
  | 'loyalty'
  | 'contracts'
  | 'hr'
  | 'payroll'

export const PERMISSION_GROUPS: Record<PermissionGroup, { en: string; ar: string }> = {
  administration: { en: 'Administration', ar: 'الإدارة' },
  crm: { en: 'Customers', ar: 'العملاء' },
  sales: { en: 'Sales and receivables', ar: 'المبيعات والذمم' },
  pos: { en: 'Point of sale', ar: 'نقاط البيع' },
  purchasing: { en: 'Purchasing', ar: 'المشتريات' },
  catalog: { en: 'Products', ar: 'المنتجات' },
  inventory: { en: 'Inventory', ar: 'المخزون' },
  accounting: { en: 'Accounting', ar: 'المحاسبة' },
  expenses: { en: 'Expenses', ar: 'المصروفات' },
  workflows: { en: 'Approval workflows', ar: 'مسارات الاعتماد' },
  tasks: { en: 'Tasks', ar: 'المهام' },
  service: { en: 'Service jobs and visits', ar: 'أوامر الخدمة والزيارات' },
  projects: { en: 'Projects', ar: 'المشاريع' },
  ai: { en: 'AI assistant', ar: 'المساعد الذكي' },
  loyalty: { en: 'Loyalty points', ar: 'نقاط الولاء' },
  contracts: { en: 'Maintenance contracts', ar: 'عقود الصيانة' },
  hr: { en: 'Human resources', ar: 'الموارد البشرية' },
  payroll: { en: 'Payroll', ar: 'الرواتب' },
}

export const PERMISSIONS = {
  'users.read': { group: 'administration', en: 'View users', ar: 'عرض المستخدمين' },
  'users.manage': { group: 'administration', en: 'Create and edit users', ar: 'إنشاء وتعديل المستخدمين' },
  'roles.read': { group: 'administration', en: 'View roles', ar: 'عرض الأدوار' },
  'roles.manage': { group: 'administration', en: 'Create and edit roles', ar: 'إنشاء وتعديل الأدوار' },
  'teams.manage': {
    group: 'administration',
    en: 'Create and edit teams and their members',
    ar: 'إنشاء الفرق وتعديلها وإدارة أعضائها',
  },
  'orgUnits.manage': {
    group: 'administration',
    en: 'Manage the organisation structure and work locations',
    ar: 'إدارة الهيكل التنظيمي ومواقع العمل',
  },
  'audit.read': { group: 'administration', en: 'View the audit log', ar: 'عرض سجل التدقيق' },
  'messaging.manage': {
    group: 'administration',
    en: 'Manage messages and templates',
    ar: 'إدارة الرسائل والقوالب',
  },
  'privacy.manage': {
    group: 'administration',
    en: 'Manage data retention and legal holds',
    ar: 'إدارة الاحتفاظ بالبيانات والحجز القانوني',
  },
  'privacy.dsr': {
    group: 'administration',
    en: 'Handle personal-data requests',
    ar: 'معالجة طلبات البيانات الشخصية',
  },

  'customers.read': { group: 'crm', en: 'View customers', ar: 'عرض العملاء' },
  'customers.create': { group: 'crm', en: 'Create customers', ar: 'إنشاء العملاء' },
  'customers.update': { group: 'crm', en: 'Edit customers', ar: 'تعديل العملاء' },
  'customers.delete': { group: 'crm', en: 'Archive customers', ar: 'أرشفة العملاء' },
  'customers.export': { group: 'crm', en: 'Export customers', ar: 'تصدير العملاء' },
  'customerGroups.manage': { group: 'crm', en: 'Manage customer groups', ar: 'إدارة مجموعات العملاء' },

  'suppliers.read': { group: 'purchasing', en: 'View suppliers', ar: 'عرض الموردين' },
  'suppliers.create': { group: 'purchasing', en: 'Create suppliers', ar: 'إنشاء الموردين' },
  'suppliers.update': { group: 'purchasing', en: 'Edit suppliers', ar: 'تعديل الموردين' },
  'suppliers.delete': { group: 'purchasing', en: 'Archive suppliers', ar: 'أرشفة الموردين' },
  'suppliers.export': { group: 'purchasing', en: 'Export suppliers', ar: 'تصدير الموردين' },
  'supplierGroups.manage': {
    group: 'purchasing',
    en: 'Manage supplier groups',
    ar: 'إدارة مجموعات الموردين',
  },
  'purchasing.read': {
    group: 'purchasing',
    en: 'View purchases, supplier payments, returns and statements',
    ar: 'عرض المشتريات ومدفوعات الموردين والمرتجعات والكشوف',
  },
  'purchasing.export': {
    group: 'purchasing',
    en: 'Export purchases, supplier payments, returns and refunds',
    ar: 'تصدير المشتريات ومدفوعات الموردين والمرتجعات والمبالغ المستردة',
  },
  'purchases.create': {
    group: 'purchasing',
    en: 'Create and edit draft purchases',
    ar: 'إنشاء وتعديل مسودات المشتريات',
  },
  'purchases.post': {
    group: 'purchasing',
    en: 'Post purchases (receive goods)',
    ar: 'ترحيل المشتريات (استلام البضاعة)',
  },
  'purchases.void': { group: 'purchasing', en: 'Void purchases', ar: 'إلغاء المشتريات' },
  'purchases.approve': {
    group: 'purchasing',
    en: 'Decide purchase approvals (when named in an approval step)',
    ar: 'البت في اعتماد المشتريات (عند التسمية في خطوة اعتماد)',
  },
  'supplierPayments.create': {
    group: 'purchasing',
    en: 'Record and allocate supplier payments',
    ar: 'تسجيل وتخصيص مدفوعات الموردين',
  },
  'supplierPayments.void': {
    group: 'purchasing',
    en: 'Void supplier payments',
    ar: 'إلغاء مدفوعات الموردين',
  },
  'issuedCheques.manage': {
    group: 'purchasing',
    en: 'Follow up cheques issued: clear, bounce, cancel',
    ar: 'متابعة الشيكات الصادرة: الصرف والارتداد والإلغاء',
  },
  'supplierReturns.create': {
    group: 'purchasing',
    en: 'Create supplier returns and allowances and apply them',
    ar: 'إنشاء مرتجعات ومسموحات الموردين وتطبيقها',
  },
  'supplierReturns.post': {
    group: 'purchasing',
    en: 'Post supplier returns and allowances',
    ar: 'ترحيل مرتجعات ومسموحات الموردين',
  },
  'supplierReturns.void': {
    group: 'purchasing',
    en: 'Void supplier returns and allowances',
    ar: 'إلغاء مرتجعات ومسموحات الموردين',
  },
  'supplierRefunds.create': {
    group: 'purchasing',
    en: 'Record refunds of supplier credit',
    ar: 'تسجيل استرداد أرصدة الموردين',
  },
  'supplierRefunds.void': {
    group: 'purchasing',
    en: 'Void supplier refunds',
    ar: 'إلغاء المبالغ المستردة من الموردين',
  },

  'sales.read': {
    group: 'sales',
    en: 'View quotations, invoices, receipts and statements',
    ar: 'عرض عروض الأسعار والفواتير والمقبوضات والكشوف',
  },
  'sales.export': {
    group: 'sales',
    en: 'Export invoices and other sales documents',
    ar: 'تصدير الفواتير ومستندات المبيعات الأخرى',
  },
  'invoices.create': {
    group: 'sales',
    en: 'Create and edit draft invoices',
    ar: 'إنشاء وتعديل مسودات الفواتير',
  },
  'invoices.post': { group: 'sales', en: 'Post invoices', ar: 'ترحيل الفواتير' },
  'invoices.void': { group: 'sales', en: 'Void invoices', ar: 'إلغاء الفواتير' },
  'invoices.send': { group: 'sales', en: 'E-mail invoices', ar: 'إرسال الفواتير بالبريد الإلكتروني' },
  'receipts.create': { group: 'sales', en: 'Record and allocate receipts', ar: 'تسجيل وتخصيص المقبوضات' },
  'receipts.void': { group: 'sales', en: 'Void receipts', ar: 'إلغاء المقبوضات' },
  'receivedCheques.manage': {
    group: 'sales',
    en: 'Follow up cheques received: deposit, clear, bounce, hand back',
    ar: 'متابعة الشيكات الواردة: الإيداع والتحصيل والارتداد والإعادة',
  },
  'creditNotes.create': {
    group: 'sales',
    en: 'Create credit notes and apply them',
    ar: 'إنشاء الإشعارات الدائنة وتطبيقها',
  },
  'creditNotes.post': {
    group: 'sales',
    en: 'Post credit notes and returns',
    ar: 'ترحيل الإشعارات الدائنة والمرتجعات',
  },
  'creditNotes.void': { group: 'sales', en: 'Void credit notes', ar: 'إلغاء الإشعارات الدائنة' },
  'refunds.create': {
    group: 'sales',
    en: 'Refund customer credit',
    ar: 'رد أرصدة العملاء',
  },
  'refunds.void': { group: 'sales', en: 'Void customer refunds', ar: 'إلغاء المبالغ المردودة للعملاء' },
  'quotations.create': {
    group: 'sales',
    en: 'Create and edit quotations',
    ar: 'إنشاء وتعديل عروض الأسعار',
  },
  'quotations.approve': {
    group: 'sales',
    en: 'Approve and reject quotations',
    ar: 'اعتماد ورفض عروض الأسعار',
  },
  'quotations.convert': {
    group: 'sales',
    en: 'Convert quotations to invoices',
    ar: 'تحويل عروض الأسعار إلى فواتير',
  },
  'publicLinks.manage': {
    group: 'sales',
    en: 'Share documents by public link and revoke links',
    ar: 'مشاركة المستندات برابط عام وإلغاء الروابط',
  },

  'pos.sell': {
    group: 'pos',
    en: 'Open own till shifts and sell',
    ar: 'فتح ورديات الصندوق الخاصة والبيع',
  },
  'pos.view_shift': {
    group: 'pos',
    en: 'See expected cash and other cashiers’ shifts',
    ar: 'عرض النقد المتوقع وورديات الصرافين الآخرين',
  },
  'pos.manage_shifts': {
    group: 'pos',
    en: 'Close other cashiers’ shifts',
    ar: 'إغلاق ورديات الصرافين الآخرين',
  },
  'pos.manage': { group: 'pos', en: 'Manage tills', ar: 'إدارة نقاط البيع' },
  'pos.price_override': {
    group: 'pos',
    en: 'Change prices at the till',
    ar: 'تغيير الأسعار في نقطة البيع',
  },
  'pos.void': { group: 'pos', en: 'Void till sales', ar: 'إلغاء مبيعات نقطة البيع' },
  'pos.return': {
    group: 'pos',
    en: 'Take returns and refund at the till',
    ar: 'استلام المرتجعات والرد في نقطة البيع',
  },
  'pos.return_unlinked': {
    group: 'pos',
    en: 'Take returns without a till sale and refund at the till',
    ar: 'استلام مرتجعات بدون فاتورة نقطة بيع والرد في نقطة البيع',
  },
  'pos.refund_any_tender': {
    group: 'pos',
    en: 'Refund till sales in cash whatever they were paid by',
    ar: 'رد مبالغ مبيعات نقطة البيع نقدًا أيًّا كانت طريقة دفعها',
  },
  'pos.cash_movement': {
    group: 'pos',
    en: 'Pay cash into and out of the till',
    ar: 'إيداع وسحب النقد من الصندوق',
  },
  'settings.manage': { group: 'administration', en: 'Change company settings', ar: 'تغيير إعدادات الشركة' },
  'workflows.read': {
    group: 'workflows',
    en: 'View approval workflows',
    ar: 'عرض مسارات الاعتماد',
  },
  'workflows.edit': {
    group: 'workflows',
    en: 'Create and edit draft approval workflows',
    ar: 'إنشاء وتعديل مسودات مسارات الاعتماد',
  },
  'workflows.simulate': {
    group: 'workflows',
    en: 'Test approval workflows (read-only simulation)',
    ar: 'اختبار مسارات الاعتماد (محاكاة للقراءة فقط)',
  },
  'workflows.publish': {
    group: 'workflows',
    en: 'Publish approval workflows and set their order',
    ar: 'نشر مسارات الاعتماد وتحديد ترتيبها',
  },
  'workflows.disable': {
    group: 'workflows',
    en: 'Disable and re-enable approval workflows',
    ar: 'تعطيل مسارات الاعتماد وإعادة تفعيلها',
  },
  'workflows.runs': {
    group: 'workflows',
    en: 'Monitor approval runs',
    ar: 'متابعة طلبات الاعتماد',
  },
  'workflows.reassign': {
    group: 'workflows',
    en: 'Reassign approval tasks and refresh approvers',
    ar: 'إعادة إسناد مهام الاعتماد وتحديث المعتمدين',
  },
  'workflows.retry': {
    group: 'workflows',
    en: 'Retry a failed final action of an approval',
    ar: 'إعادة محاولة الإجراء النهائي الفاشل للاعتماد',
  },
  'workflows.cancel': {
    group: 'workflows',
    en: 'Cancel approval runs',
    ar: 'إلغاء طلبات الاعتماد',
  },
  'workflows.delegations': {
    group: 'workflows',
    en: 'Manage other users’ approval delegations',
    ar: 'إدارة تفويضات الاعتماد للمستخدمين الآخرين',
  },
  'tasks.read': {
    group: 'tasks',
    en: 'Use tasks: My work, the tasks one can see, and work on one’s own',
    ar: 'استخدام المهام: أعمالي والمهام المتاحة والعمل على المهام الخاصة',
  },
  'tasks.create': { group: 'tasks', en: 'Create tasks', ar: 'إنشاء المهام' },
  'tasks.manage_all': {
    group: 'tasks',
    en: 'Assign, reschedule, cancel and reopen any task one can see',
    ar: 'إسناد أي مهمة متاحة وإعادة جدولتها وإلغاؤها وإعادة فتحها',
  },
  'tasks.view_all': {
    group: 'tasks',
    en: 'See every task, including personal and restricted ones',
    ar: 'عرض جميع المهام بما فيها الشخصية والمقيدة',
  },
  'tasks.reports': { group: 'tasks', en: 'View task reports', ar: 'عرض تقارير المهام' },
  'tasks.export': { group: 'tasks', en: 'Export the task register', ar: 'تصدير سجل المهام' },
  'service.read': {
    group: 'service',
    en: 'View service jobs, visits and the schedule',
    ar: 'عرض أوامر الخدمة والزيارات والجدول',
  },
  'service.create': {
    group: 'service',
    en: 'Open service jobs and edit their details and locations',
    ar: 'فتح أوامر الخدمة وتعديل بياناتها ومواقعها',
  },
  'service.schedule': {
    group: 'service',
    en: 'Schedule, reschedule and cancel visits',
    ar: 'جدولة الزيارات وإعادة جدولتها وإلغاؤها',
  },
  'service.work': {
    group: 'service',
    en: 'Carry out the visits of one’s teams: items, price approval, work done, outcome',
    ar: 'تنفيذ زيارات فرقه: البنود وموافقة السعر والعمل المنجز والنتيجة',
  },
  'service.approve': {
    group: 'service',
    en: 'Approve or return follow-up visit requests',
    ar: 'اعتماد طلبات زيارات المتابعة أو إعادتها',
  },
  'service.invoice': {
    group: 'service',
    en: 'Issue invoices for completed service work',
    ar: 'إصدار فواتير أعمال الخدمة المنجزة',
  },
  'service.collect': {
    group: 'service',
    en: 'Record payments collected from customers',
    ar: 'تسجيل المبالغ المحصلة من العملاء',
  },
  'service.verify_payments': {
    group: 'service',
    en: 'Verify or reject collected payments (records receipts)',
    ar: 'التحقق من المبالغ المحصلة أو رفضها (يسجل سندات القبض)',
  },
  'service.assets': {
    group: 'service',
    en: 'Manage customer sites, their places and equipment (and import them)',
    ar: 'إدارة مواقع العملاء وأماكنها ومعداتها (واستيرادها)',
  },
  'service.configure': {
    group: 'service',
    en: 'Set up site templates and equipment types',
    ar: 'إعداد قوالب المواقع وأنواع المعدات',
  },
  'service.manage': {
    group: 'service',
    en: 'Act on any visit and cancel service jobs',
    ar: 'التصرف في أي زيارة وإلغاء أوامر الخدمة',
  },
  // Projects (ADR-039). Delivery and money are separate: `projects.read` shows no amounts.
  'projects.read': {
    group: 'projects',
    en: 'View projects: plan, team, notes, files and tasks (no amounts)',
    ar: 'عرض المشاريع: الخطة والفريق والملاحظات والملفات والمهام (دون المبالغ)',
  },
  'projects.create': { group: 'projects', en: 'Create projects', ar: 'إنشاء المشاريع' },
  'projects.manage': {
    group: 'projects',
    en: 'Change any project one can see: details, plan, team and status',
    ar: 'تعديل أي مشروع متاح: البيانات والخطة والفريق والحالة',
  },
  'projects.financials': {
    group: 'projects',
    en: 'See projects’ money: contract value, budgets, costs, billing and margins',
    ar: 'عرض الجوانب المالية للمشاريع: قيمة العقد والموازنات والتكاليف والفوترة والهوامش',
  },
  'projects.configure': {
    group: 'projects',
    en: 'Manage project types and their templates',
    ar: 'إدارة أنواع المشاريع وقوالبها',
  },
  'projects.export': { group: 'projects', en: 'Export the project list', ar: 'تصدير قائمة المشاريع' },
  'projects.budgets': {
    group: 'projects',
    en: 'Prepare and submit project budgets (and set them directly when approval is off)',
    ar: 'إعداد موازنات المشاريع وتقديمها (واعتمادها مباشرة عند إيقاف الاعتماد)',
  },
  'projects.budgets_approve': {
    group: 'projects',
    en: 'Approve project budgets prepared by others',
    ar: 'اعتماد موازنات المشاريع التي يعدها الآخرون',
  },
  'projects.materials': {
    group: 'projects',
    en: 'Issue stock to projects and take it back',
    ar: 'صرف المخزون للمشاريع وإرجاعه',
  },
  'projects.bill': {
    group: 'projects',
    en: 'Invoice projects through Sales: billing plan items and re-billed costs, and void those invoices',
    ar: 'فوترة المشاريع عبر المبيعات: بنود خطة الفوترة والتكاليف المعاد فوترتها، وإلغاء تلك الفواتير',
  },
  'projects.reports': {
    group: 'projects',
    en: 'View project reports: profitability, budget against actual, unbilled work',
    ar: 'عرض تقارير المشاريع: الربحية والموازنة مقابل الفعلي والأعمال غير المفوترة',
  },
  'projects.contract': {
    group: 'projects',
    en: 'Run project contracts: terms, bill of quantities, variations and progress claims',
    ar: 'إدارة عقود المشاريع: الشروط وجدول الكميات والأوامر التغييرية والمستخلصات',
  },
  'projects.contract_approve': {
    group: 'projects',
    en: 'Approve variations and claims internally before they go to the client',
    ar: 'الموافقة الداخلية على الأوامر التغييرية والمستخلصات قبل تقديمها إلى العميل',
  },
  'projects.variations_approve': {
    group: 'projects',
    en: 'Record the client’s decision on variations',
    ar: 'تسجيل قرار العميل بشأن الأوامر التغييرية',
  },
  'projects.claims_certify': {
    group: 'projects',
    en: 'Record certified progress claims (creates the tax invoice), reject and void them',
    ar: 'تسجيل اعتماد المستخلصات (ينشئ الفاتورة الضريبية) ورفضها وإلغاؤها',
  },
  'ai.use': {
    group: 'ai',
    en: 'Use the AI assistant on the records you can already see',
    ar: 'استخدام المساعد الذكي على السجلات التي يمكنك الاطلاع عليها',
  },
  'ai.manage': {
    group: 'ai',
    en: 'Manage the AI assistant: the company key, model, limits, features and usage',
    ar: 'إدارة المساعد الذكي: مفتاح الشركة والنموذج والحدود والميزات والاستخدام',
  },
  'projects.cost_entries': {
    group: 'projects',
    en: 'Record project cost entries: allocations, transfers between projects and opening costs',
    ar: 'تسجيل قيود تكاليف المشاريع: التحميل والنقل بين المشاريع والتكاليف الافتتاحية',
  },
  'projects.time': {
    group: 'projects',
    en: 'Log your own time on the projects you are a member of',
    ar: 'تسجيل وقتك على المشاريع التي أنت عضو فيها',
  },
  'projects.time_approve': {
    group: 'projects',
    en: 'Approve or return other people’s time on projects',
    ar: 'الموافقة على وقت الآخرين في المشاريع أو إعادته',
  },
  'projects.rates': {
    group: 'projects',
    en: 'See and set what each person’s hour costs (salary-sensitive)',
    ar: 'عرض وتحديد تكلفة ساعة كل شخص (معلومات حساسة مرتبطة بالرواتب)',
  },
  'settings.manage_secrets': {
    group: 'administration',
    en: 'Set and remove integration keys (write-only)',
    ar: 'تعيين مفاتيح التكامل وإزالتها (كتابة فقط)',
  },
  'products.read': { group: 'catalog', en: 'View products', ar: 'عرض المنتجات' },
  'products.create': { group: 'catalog', en: 'Create products', ar: 'إنشاء المنتجات' },
  'products.update': { group: 'catalog', en: 'Edit products and prices', ar: 'تعديل المنتجات والأسعار' },
  'products.delete': { group: 'catalog', en: 'Archive products', ar: 'أرشفة المنتجات' },
  'products.export': { group: 'catalog', en: 'Export the product list', ar: 'تصدير قائمة المنتجات' },
  'catalog.manage': {
    group: 'catalog',
    en: 'Manage units, categories and attributes',
    ar: 'إدارة الوحدات والفئات والخصائص',
  },

  'inventory.read': { group: 'inventory', en: 'View stock and movements', ar: 'عرض المخزون والحركات' },
  'inventory.adjust': { group: 'inventory', en: 'Adjust and count stock', ar: 'تسوية وجرد المخزون' },
  'inventory.transfer': {
    group: 'inventory',
    en: 'Transfer stock between warehouses',
    ar: 'تحويل المخزون بين المستودعات',
  },
  'warehouses.manage': { group: 'inventory', en: 'Manage warehouses', ar: 'إدارة المستودعات' },
  'ledger.read': { group: 'accounting', en: 'View accounts and journal entries', ar: 'عرض الحسابات والقيود' },
  'accounts.manage': { group: 'accounting', en: 'Manage the chart of accounts', ar: 'إدارة دليل الحسابات' },
  'journals.post': { group: 'accounting', en: 'Post manual journal entries', ar: 'ترحيل القيود اليدوية' },
  'journals.reverse': { group: 'accounting', en: 'Reverse journal entries', ar: 'عكس القيود' },
  'fiscalYears.manage': { group: 'accounting', en: 'Manage fiscal years', ar: 'إدارة السنوات المالية' },
  'reports.financial': { group: 'accounting', en: 'View financial reports', ar: 'عرض التقارير المالية' },
  // Administration, not Accounting: Sales, Purchasing and POS keep using tax rates when Accounting is off
  // (ADR-024).
  'taxes.manage': { group: 'administration', en: 'Manage tax rates', ar: 'إدارة نسب الضريبة' },
  'treasury.read': {
    group: 'accounting',
    en: 'View cash and bank accounts',
    ar: 'عرض الحسابات النقدية والبنكية',
  },
  'treasury.manage': {
    group: 'accounting',
    en: 'Manage cash and bank accounts and transfers',
    ar: 'إدارة الحسابات النقدية والتحويلات',
  },
  'treasury.reconcile': {
    group: 'accounting',
    en: 'Reconcile bank statements (match, adjust, complete)',
    ar: 'تسوية كشوف الحسابات البنكية (المطابقة والتسوية والإقفال)',
  },
  'treasury.reconcile_reopen': {
    group: 'accounting',
    en: 'Reopen completed bank reconciliations',
    ar: 'إعادة فتح التسويات البنكية المكتملة',
  },

  // Sales commission and technician incentives (ADR-049): part of Sales for every company.
  'commissions.read': {
    group: 'sales',
    en: 'View commission plans, runs and every earner’s statements',
    ar: 'عرض خطط العمولات وكشوفها وبيانات عمولة جميع المستحقين',
  },
  'commissions.manage': {
    group: 'sales',
    en: 'Set up commission plans, their rates, tiers and members',
    ar: 'إعداد خطط العمولات ونسبها وشرائحها وأعضائها',
  },
  'commissions.prepare': {
    group: 'sales',
    en: 'Create and calculate monthly commission runs',
    ar: 'إنشاء كشوف العمولات الشهرية واحتسابها',
  },
  'commissions.approve': {
    group: 'sales',
    en: 'Approve commission runs (posts the commission journal)',
    ar: 'اعتماد كشوف العمولات (ترحيل قيد العمولات)',
  },
  'commissions.pay': {
    group: 'sales',
    en: 'Pay commission statements from a cash or bank account',
    ar: 'صرف العمولات المعتمدة من حساب نقدي أو بنكي',
  },

  'expenses.read': { group: 'expenses', en: 'View expenses', ar: 'عرض المصروفات' },
  'expenses.create': { group: 'expenses', en: 'Record expenses', ar: 'تسجيل المصروفات' },
  'expenses.update': { group: 'expenses', en: 'Edit expenses', ar: 'تعديل المصروفات' },
  'expenses.void': { group: 'expenses', en: 'Void expenses', ar: 'إلغاء المصروفات' },
  'expenseCategories.manage': {
    group: 'expenses',
    en: 'Manage expense categories',
    ar: 'إدارة فئات المصروفات',
  },

  // Loyalty points (ADR-044): an opt-in module the operator switches on per company.
  'loyalty.read': {
    group: 'loyalty',
    en: 'View loyalty members, their points and the programme',
    ar: 'عرض أعضاء برنامج الولاء ونقاطهم والبرنامج',
  },
  'loyalty.enroll': {
    group: 'loyalty',
    en: 'Enrol customers in the loyalty programme and block or unblock members',
    ar: 'تسجيل العملاء في برنامج الولاء وإيقاف الأعضاء أو إعادة تفعيلهم',
  },
  'loyalty.redeem': {
    group: 'loyalty',
    en: 'Redeem customers’ points at the till',
    ar: 'استبدال نقاط العملاء في نقطة البيع',
  },
  'loyalty.adjust': {
    group: 'loyalty',
    en: 'Add or take away points by hand, with a reason',
    ar: 'إضافة النقاط أو خصمها يدويًا مع ذكر السبب',
  },
  'loyalty.manage': {
    group: 'loyalty',
    en: 'Set up the loyalty programme: earning, redeeming, expiry, tiers and bonus rules',
    ar: 'إعداد برنامج الولاء: الكسب والاستبدال والانتهاء والمستويات وقواعد المكافآت',
  },

  // Maintenance contracts (Service C2–C4): an opt-in module the operator switches on per company.
  'contracts.read': {
    group: 'contracts',
    en: 'View maintenance contracts, their coverage, plans and planned visits (without amounts)',
    ar: 'عرض عقود الصيانة وتغطيتها وخططها والزيارات الوقائية (دون المبالغ)',
  },
  'contracts.manage': {
    group: 'contracts',
    en: 'Create, edit, activate, renew and terminate maintenance contracts and their plans',
    ar: 'إنشاء عقود الصيانة وتعديلها وتفعيلها وتجديدها وإنهاؤها وإدارة خططها',
  },
  'contracts.bill': {
    group: 'contracts',
    en: 'Manage contract instalments and invoice them',
    ar: 'إدارة أقساط العقود وإصدار فواتيرها',
  },
  'contracts.approve': {
    group: 'contracts',
    en: 'Approve maintenance contracts before they start',
    ar: 'اعتماد عقود الصيانة قبل بدئها',
  },

  // Human resources (ADR-048): an opt-in module the operator switches on per company.
  'employees.read': {
    group: 'hr',
    en: 'View employees, their contracts, documents and history (without pay)',
    ar: 'عرض الموظفين وعقودهم ووثائقهم وسجلهم (دون الرواتب)',
  },
  'employees.hire': {
    group: 'hr',
    en: 'Hire employees',
    ar: 'تعيين الموظفين',
  },
  'employees.manage': {
    group: 'hr',
    en: 'Edit employees, change contracts, confirm probation, suspend, reinstate and end employment',
    ar: 'تعديل بيانات الموظفين وتغيير العقود وتثبيتهم بعد التجربة وإيقافهم وإعادتهم وإنهاء خدمتهم',
  },
  'employees.view_pay': {
    group: 'hr',
    en: 'See employee salaries and bank accounts (with "Edit employees": change salaries)',
    ar: 'عرض رواتب الموظفين وحساباتهم البنكية (ومع "تعديل الموظفين": تغيير الرواتب)',
  },
  'employees.documents': {
    group: 'hr',
    en: 'Add, renew and cancel employee documents',
    ar: 'إضافة وثائق الموظفين وتجديدها وإلغاؤها',
  },
  'payComponents.manage': {
    group: 'hr',
    en: 'Set up the salary structure (pay components)',
    ar: 'إعداد هيكل الرواتب (عناصر الراتب)',
  },

  // Payroll (ADR-048): an opt-in module the operator switches on per company; it needs Human resources.
  'payroll.read': {
    group: 'payroll',
    en: 'View payroll runs, payslips and the social contribution schedule',
    ar: 'عرض كشوف الرواتب وقسائم الرواتب وجدول اشتراكات الحماية الاجتماعية',
  },
  'payroll.prepare': {
    group: 'payroll',
    en: 'Create payroll runs, enter absences, overtime and adjustments, and calculate',
    ar: 'إنشاء كشوف الرواتب وإدخال الغياب والعمل الإضافي والتسويات واحتسابها',
  },
  'payroll.approve': {
    group: 'payroll',
    en: 'Approve payroll runs (posts the salary journal) and reverse them before payment',
    ar: 'اعتماد كشوف الرواتب (ترحيل قيد الرواتب) وعكسها قبل الصرف',
  },
  'payroll.pay': {
    group: 'payroll',
    en: 'Export the WPS salary file and record salary payment',
    ar: 'تصدير ملف حماية الأجور وتسجيل صرف الرواتب',
  },
  'payroll.settings': {
    group: 'payroll',
    en: 'Set up payroll and the social contribution rules',
    ar: 'إعداد الرواتب ونسب اشتراكات الحماية الاجتماعية',
  },
} as const satisfies Record<string, PermissionDef>

export type Permission = keyof typeof PERMISSIONS

export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[]

export function isPermission(value: string): value is Permission {
  return Object.hasOwn(PERMISSIONS, value)
}

/**
 * The permissions whose readers and commands honour data scopes (ADR-025), by scoped resource (the
 * API's `ScopeFilter` keys; its users-roles test keeps the two equal). A scope other than ALL narrows
 * only these. Every other permission still reaches every record whatever its scope says: customers
 * (master data), treasury, inventory and the till (`pos.*`) stay tenant-wide. Purchases: the
 * documents, their commands and the purchase a supplier return names (ASM-143). Projects: every
 * project permission that reads or acts on a project, not `projects.configure`, `projects.reports`
 * (rows follow the read permissions) or `projects.rates`. Sales documents (ASM-256): each document
 * type's commands under its own resource; `sales.read` and `sales.export`, listed once under
 * `sales_invoice`, confine every sales document type, and their readers that cover every record
 * (aging, statements, balances, reports) refuse a scoped reader. Employees (ADR-048): every employee
 * permission; the HR dashboard and expiry lists count the employees in scope. Task permissions are not listed: a
 * task's linked record follows that record's own read permission.
 */
export const SCOPE_ENFORCED_BY_RESOURCE = {
  purchase: [
    'purchasing.read',
    'purchasing.export',
    'purchases.create',
    'purchases.post',
    'purchases.void',
    'supplierReturns.create',
    'supplierReturns.post',
  ],
  project: [
    'projects.read',
    'projects.create',
    'projects.manage',
    'projects.export',
    'projects.financials',
    'projects.budgets',
    'projects.budgets_approve',
    'projects.materials',
    'projects.cost_entries',
    'projects.bill',
    'projects.contract',
    'projects.contract_approve',
    'projects.variations_approve',
    'projects.claims_certify',
    'projects.time',
    'projects.time_approve',
  ],
  sales_invoice: [
    'sales.read',
    'sales.export',
    'invoices.create',
    'invoices.post',
    'invoices.void',
    'invoices.send',
  ],
  quotation: ['quotations.create', 'quotations.approve', 'quotations.convert'],
  credit_note: ['creditNotes.create', 'creditNotes.post', 'creditNotes.void'],
  receipt: ['receipts.create', 'receipts.void'],
  customer_refund: ['refunds.create', 'refunds.void'],
  // ADR-048: a head of unit granted the employee permissions on their unit sees and keeps its staff.
  employee: [
    'employees.read',
    'employees.hire',
    'employees.manage',
    'employees.view_pay',
    'employees.documents',
  ],
} as const satisfies Record<string, readonly Permission[]>

/** Every permission a data scope narrows (the union of `SCOPE_ENFORCED_BY_RESOURCE`), sorted. */
export const SCOPE_ENFORCED_PERMISSIONS: readonly Permission[] = [
  ...new Set<Permission>(Object.values(SCOPE_ENFORCED_BY_RESOURCE).flat()),
].sort()

const scopeEnforced: ReadonlySet<Permission> = new Set(SCOPE_ENFORCED_PERMISSIONS)

/** A scope other than ALL narrows what `permission` reaches (see `SCOPE_ENFORCED_BY_RESOURCE`). */
export function isScopeEnforced(permission: Permission): boolean {
  return scopeEnforced.has(permission)
}

/**
 * Who may search products for document lines and the till (`GET /product-variants/lookup`): any one
 * of these. The route declares exactly this list, and the web's line editors show the product
 * picker by the same rule, so a clerk who may create invoices, quotations or credit notes picks
 * products without `products.read` (catalogue management).
 */
export const VARIANT_LOOKUP_PERMISSIONS = [
  'products.read',
  'invoices.create',
  'quotations.create',
  'creditNotes.create',
  'purchases.create',
  'supplierReturns.create',
  'inventory.read',
  'inventory.adjust',
  'inventory.transfer',
  'pos.sell',
  'service.create',
  'service.work',
  'projects.materials',
  // Loyalty bonus rules name a product or a variant (ADR-044).
  'loyalty.manage',
] as const satisfies readonly [Permission, ...Permission[]]

/** A prerequisite: one permission, or a list of which any one will do. */
export type PermissionPrerequisite = Permission | readonly Permission[]

/**
 * What an action needs beside it to be usable: the view permission of the screens that hold it, or,
 * for an approval, the subject's read permission the approver resolver demands (workflows.ts
 * `readPermission`). A role may still be saved with a gap; the role form warns and offers to fill it.
 * Actions missing here work on their own (their page accepts them, e.g. `invoices.create`, `pos.sell`).
 */
export const PERMISSION_PREREQUISITES: Partial<Record<Permission, readonly PermissionPrerequisite[]>> = {
  'users.manage': ['users.read'],
  'roles.manage': ['roles.read'],

  'customers.create': ['customers.read'],
  'customers.update': ['customers.read'],
  'customers.delete': ['customers.read'],
  'customers.export': ['customers.read'],
  'customerGroups.manage': ['customers.read'],

  'suppliers.create': ['suppliers.read'],
  'suppliers.update': ['suppliers.read'],
  'suppliers.delete': ['suppliers.read'],
  'suppliers.export': ['suppliers.read'],
  'supplierGroups.manage': ['suppliers.read'],
  'purchasing.export': ['purchasing.read'],
  'purchases.create': ['purchasing.read'],
  'purchases.post': ['purchasing.read'],
  'purchases.void': ['purchasing.read'],
  'purchases.approve': ['purchasing.read'],
  'supplierPayments.create': ['purchasing.read'],
  'supplierPayments.void': ['purchasing.read'],
  'issuedCheques.manage': ['purchasing.read'],
  'supplierReturns.create': ['purchasing.read'],
  'supplierReturns.post': ['purchasing.read'],
  'supplierReturns.void': ['purchasing.read'],
  'supplierRefunds.create': ['purchasing.read'],
  'supplierRefunds.void': ['purchasing.read'],

  'sales.export': ['sales.read'],
  'invoices.post': ['sales.read'],
  'invoices.void': ['sales.read'],
  'invoices.send': ['sales.read'],
  'receipts.create': ['sales.read'],
  'receipts.void': ['sales.read'],
  'receivedCheques.manage': ['sales.read'],
  'creditNotes.create': ['sales.read'],
  'creditNotes.post': ['sales.read'],
  'creditNotes.void': ['sales.read'],
  'refunds.create': ['sales.read'],
  'refunds.void': ['sales.read'],
  'quotations.approve': ['sales.read'],
  'quotations.convert': ['sales.read'],
  'publicLinks.manage': ['sales.read'],

  'pos.price_override': ['pos.sell'],
  'pos.cash_movement': ['pos.sell'],
  'pos.void': [['pos.sell', 'pos.view_shift', 'pos.manage_shifts']],
  'pos.return': [['pos.sell', 'pos.view_shift', 'pos.manage_shifts']],
  'pos.return_unlinked': [['pos.sell', 'pos.view_shift', 'pos.manage_shifts']],
  // Widens what a return against a till sale may refund in cash (BR-POS-012): nothing without pos.return.
  'pos.refund_any_tender': ['pos.return'],

  'workflows.edit': ['workflows.read'],
  'workflows.simulate': ['workflows.read'],
  'workflows.publish': ['workflows.read'],
  'workflows.disable': ['workflows.read'],
  'workflows.reassign': ['workflows.runs'],
  'workflows.retry': ['workflows.runs'],
  'workflows.cancel': ['workflows.runs'],

  'tasks.create': ['tasks.read'],
  'tasks.manage_all': ['tasks.read'],
  'tasks.view_all': ['tasks.read'],
  'tasks.export': ['tasks.read'],

  'service.create': ['service.read'],
  'service.schedule': ['service.read'],
  'service.work': ['service.read'],
  'service.approve': ['service.read'],
  'service.invoice': ['service.read'],
  'service.collect': ['service.read'],
  'service.verify_payments': ['service.read'],
  'service.assets': ['service.read'],
  'service.manage': ['service.read'],

  'projects.create': ['projects.read'],
  'projects.manage': ['projects.read'],
  'projects.export': ['projects.read'],
  'projects.contract': ['projects.read'],
  'projects.budgets': ['projects.read', 'projects.financials'],
  'projects.budgets_approve': ['projects.financials'],
  'projects.bill': ['projects.read', 'projects.financials'],
  'projects.claims_certify': ['projects.read', ['projects.contract', 'projects.financials']],
  'projects.variations_approve': ['projects.read', ['projects.contract', 'projects.financials']],
  'projects.contract_approve': ['projects.contract'],

  'products.create': ['products.read'],
  'products.update': ['products.read'],
  'products.delete': ['products.read'],
  'products.export': ['products.read'],
  'catalog.manage': ['products.read'],
  'inventory.adjust': ['inventory.read'],
  'inventory.transfer': ['inventory.read'],
  'warehouses.manage': ['inventory.read'],

  'accounts.manage': ['ledger.read'],
  'journals.post': ['ledger.read'],
  'journals.reverse': ['ledger.read'],
  'fiscalYears.manage': ['ledger.read'],
  'treasury.manage': ['treasury.read'],
  'treasury.reconcile': ['treasury.read'],
  'treasury.reconcile_reopen': ['treasury.read'],

  'expenses.create': ['expenses.read'],
  'expenses.update': ['expenses.read'],
  'expenses.void': ['expenses.read'],
  'expenseCategories.manage': ['expenses.read'],

  'loyalty.enroll': ['loyalty.read'],
  'loyalty.redeem': ['pos.sell'],
  'loyalty.adjust': ['loyalty.read'],

  // The register and the billing queue sit under Service.
  'contracts.read': ['service.read'],
  'contracts.manage': ['service.read', 'contracts.read'],
  'contracts.bill': ['service.read', 'contracts.read'],
  'contracts.approve': ['contracts.read'],

  'commissions.manage': ['commissions.read'],
  'commissions.prepare': ['commissions.read'],
  'commissions.approve': ['commissions.read'],
  'commissions.pay': ['commissions.read'],

  'employees.hire': ['employees.read', 'employees.view_pay'],
  'employees.manage': ['employees.read'],
  'employees.view_pay': ['employees.read'],
  'employees.documents': ['employees.read'],
  'payComponents.manage': ['employees.read'],
  'payroll.read': ['employees.read'],
  'payroll.prepare': ['payroll.read'],
  'payroll.approve': ['payroll.read'],
  'payroll.pay': ['payroll.read'],
  'payroll.settings': ['payroll.read'],
}

/** Each chosen permission with the prerequisites the choice lacks (none: it is not listed). */
export function missingPrerequisites(
  chosen: Iterable<Permission>,
): { permission: Permission; missing: PermissionPrerequisite[] }[] {
  const held = new Set(chosen)
  const satisfied = (p: PermissionPrerequisite) =>
    typeof p === 'string' ? held.has(p) : p.some((q) => held.has(q))
  return [...held].sort().flatMap((permission) => {
    const missing = (PERMISSION_PREREQUISITES[permission] ?? []).filter((p) => !satisfied(p))
    return missing.length ? [{ permission, missing }] : []
  })
}

/**
 * The permissions to add so that `chosen` has no gap: the first option of an any-of list, and each
 * added permission's own prerequisites in turn (contracts.manage → contracts.read → service.read).
 */
export function withPrerequisites(chosen: Iterable<Permission>): Permission[] {
  const out = new Set(chosen)
  for (let gaps = missingPrerequisites(out); gaps.length; gaps = missingPrerequisites(out)) {
    for (const gap of gaps)
      for (const p of gap.missing) out.add(typeof p === 'string' ? p : (p[0] as Permission))
  }
  return [...out].sort()
}

/**
 * Module entitlements (D15): a module is a permission group the company's plan includes. Administration
 * is not a module; it is always on. Stored per tenant in `platform.tenants.modules` (default: all).
 */
export type ModuleKey = Exclude<PermissionGroup, 'administration'>

export const MODULES: readonly ModuleKey[] = [
  'crm',
  'sales',
  'pos',
  'purchasing',
  'catalog',
  'inventory',
  'accounting',
  'expenses',
  'workflows',
  'tasks',
  'service',
  'projects',
  'ai',
  'loyalty',
  'contracts',
  'hr',
  'payroll',
]

/**
 * Modules a company does not get by default: the operator switches them on per company (05 §9.1 for `ai`,
 * ADR-044 for `loyalty`, Service C2 for `contracts`, ADR-048 for `hr` and `payroll`). They are not in the platform.tenants.modules default
 * and were never backfilled.
 */
export const OPT_IN_MODULES: readonly ModuleKey[] = ['ai', 'loyalty', 'contracts', 'hr', 'payroll']

/** What a company created by the CLI or provisioning starts with (the database default). */
export const DEFAULT_MODULES: readonly ModuleKey[] = MODULES.filter((m) => !OPT_IN_MODULES.includes(m))

export function isModule(value: string): value is ModuleKey {
  return (MODULES as readonly string[]).includes(value)
}

/** The module a permission belongs to, or null for administration (never disabled). */
export function permissionModule(permission: Permission): ModuleKey | null {
  const group = PERMISSIONS[permission].group
  return group === 'administration' ? null : group
}

/**
 * How modules rely on each other (ADR-024). `needs`: a feature of the module cannot work without the
 * other one; `uses`: it keeps working with fewer features. The super-admin console warns (and asks for a
 * confirmation) when a module that is still on loses one of these; it never refuses a combination.
 */
export interface ModuleDependency {
  module: ModuleKey
  kind: 'needs' | 'uses'
  /** What the dependent module loses without it. */
  reason: { en: string; ar: string }
}

export const MODULE_DEPENDENCIES: Record<ModuleKey, readonly ModuleDependency[]> = {
  crm: [],
  sales: [
    {
      module: 'crm',
      kind: 'uses',
      reason: {
        en: 'Customers can still be picked, but nobody can add or correct them',
        ar: 'يمكن اختيار العملاء الحاليين لكن لا يمكن إضافتهم أو تصحيح بياناتهم',
      },
    },
    {
      module: 'catalog',
      kind: 'uses',
      reason: {
        en: 'Products can still be sold, but nobody can add them or change prices',
        ar: 'يمكن بيع المنتجات الحالية لكن لا يمكن إضافتها أو تغيير أسعارها',
      },
    },
    {
      module: 'accounting',
      kind: 'uses',
      reason: {
        en: 'Documents still post, but nobody can maintain the lock date or the books',
        ar: 'تستمر القيود لكن لا يمكن صيانة تاريخ الإقفال أو الدفاتر',
      },
    },
  ],
  pos: [
    {
      module: 'sales',
      kind: 'needs',
      reason: {
        en: 'Till sales are Sales invoices and receipts: without Sales nobody can see or correct them',
        ar: 'مبيعات نقاط البيع فواتير وسندات قبض للمبيعات: بدونها لا يمكن عرضها أو تصحيحها',
      },
    },
    {
      module: 'catalog',
      kind: 'uses',
      reason: {
        en: 'The till sells existing products only; nobody can add them or change prices',
        ar: 'تبيع نقطة البيع المنتجات الحالية فقط ولا يمكن إضافتها أو تغيير أسعارها',
      },
    },
    {
      module: 'inventory',
      kind: 'uses',
      reason: {
        en: 'Stock still moves, but nobody can count or adjust it',
        ar: 'يتحرك المخزون لكن لا يمكن جرده أو تسويته',
      },
    },
  ],
  purchasing: [
    {
      module: 'catalog',
      kind: 'uses',
      reason: {
        en: 'Existing products can be bought, but nobody can add them',
        ar: 'يمكن شراء المنتجات الحالية لكن لا يمكن إضافتها',
      },
    },
    {
      module: 'inventory',
      kind: 'uses',
      reason: {
        en: 'Stock still moves, but nobody can count or adjust it',
        ar: 'يتحرك المخزون لكن لا يمكن جرده أو تسويته',
      },
    },
    {
      module: 'accounting',
      kind: 'uses',
      reason: {
        en: 'Documents still post, but nobody can maintain the lock date or the books',
        ar: 'تستمر القيود لكن لا يمكن صيانة تاريخ الإقفال أو الدفاتر',
      },
    },
  ],
  catalog: [],
  inventory: [
    {
      module: 'catalog',
      kind: 'uses',
      reason: {
        en: 'Stock of existing products can move, but nobody can add products',
        ar: 'يمكن تحريك مخزون المنتجات الحالية لكن لا يمكن إضافة منتجات',
      },
    },
  ],
  accounting: [],
  expenses: [
    {
      module: 'accounting',
      kind: 'uses',
      reason: {
        en: 'Expenses still post, but nobody can maintain the lock date or the books',
        ar: 'تستمر قيود المصروفات لكن لا يمكن صيانة تاريخ الإقفال أو الدفاتر',
      },
    },
  ],
  /**
   * ADR-025: workflows route subjects of several modules and hold each run while its subject's module is off
   * (`SUBJECT_MODULE_OFF`), so it depends on none of them.
   */
  workflows: [],
  tasks: [],
  service: [
    {
      module: 'sales',
      kind: 'needs',
      reason: {
        en: 'Completed work is invoiced and payments are verified in Sales: without it, jobs cannot be billed',
        ar: 'تُفوتر الأعمال المنجزة وتُعتمد المبالغ في المبيعات: بدونها لا يمكن فوترة أوامر الخدمة',
      },
    },
    {
      module: 'crm',
      kind: 'uses',
      reason: {
        en: 'Existing customers can be picked, but nobody can add or correct them',
        ar: 'يمكن اختيار العملاء الحاليين لكن لا يمكن إضافتهم أو تصحيح بياناتهم',
      },
    },
    {
      module: 'catalog',
      kind: 'uses',
      reason: {
        en: 'Existing services and parts can be used, but nobody can add them or change prices',
        ar: 'يمكن استخدام الخدمات والقطع الحالية لكن لا يمكن إضافتها أو تغيير أسعارها',
      },
    },
  ],
  /** ADR-039: an internal project needs none of these, so every edge is `uses` (more join the later releases). */
  /** ADR-041: `ai` imports no module; modules register their tools and agents into it. */
  ai: [],
  /** ADR-044: points are earned on Sales invoices and redeemed against them; Sales never imports Loyalty. */
  loyalty: [
    {
      module: 'sales',
      kind: 'needs',
      reason: {
        en: 'Points are earned on sales invoices and taken back on their returns: without Sales nobody earns',
        ar: 'تُكسب النقاط على فواتير المبيعات وتُسترد مع مرتجعاتها: بدون المبيعات لا يكسب أحد نقاطًا',
      },
    },
    {
      module: 'pos',
      kind: 'uses',
      reason: {
        en: 'Members keep earning on invoices, but nobody can redeem points at a till',
        ar: 'يستمر الأعضاء في الكسب على الفواتير لكن لا يمكن استبدال النقاط في نقطة البيع',
      },
    },
    {
      module: 'crm',
      kind: 'uses',
      reason: {
        en: 'Existing customers can be enrolled, but nobody can add or correct them',
        ar: 'يمكن تسجيل العملاء الحاليين لكن لا يمكن إضافتهم أو تصحيح بياناتهم',
      },
    },
  ],
  /** ADR-048: the employee file stands alone; Payroll reads it, it never reads Payroll. */
  hr: [],
  /** ADR-048: runs pay hr's employees on hr's salaries and post their journals like every document. */
  payroll: [
    {
      module: 'hr',
      kind: 'needs',
      reason: {
        en: 'Payroll pays the employees, contracts and salaries of Human resources: without it nobody can be paid',
        ar: 'تصرف الرواتب لموظفي الموارد البشرية وفق عقودهم ورواتبهم: بدونها لا يمكن صرف راتب لأحد',
      },
    },
    {
      module: 'accounting',
      kind: 'uses',
      reason: {
        en: 'Salary and payment journals still post, but nobody can maintain the lock date or the books',
        ar: 'تستمر قيود الرواتب والصرف لكن لا يمكن صيانة تاريخ الإقفال أو الدفاتر',
      },
    },
  ],
  /** Service C2: contracts cover a site's assets and their plans make Service jobs; Service never imports them. */
  contracts: [
    {
      module: 'service',
      kind: 'needs',
      reason: {
        en: 'Contracts cover the sites and assets of Service and their plans make Service jobs: without it nothing is covered or planned',
        ar: 'تغطي العقود مواقع الخدمة ومعداتها وتُنشئ خططها أوامر خدمة: بدون الخدمة لا تُغطّى أي معدات ولا تُجدول زيارات',
      },
    },
    {
      module: 'sales',
      kind: 'uses',
      reason: {
        en: 'Contracts and plans keep working, but instalments cannot be invoiced',
        ar: 'تستمر العقود والخطط في العمل لكن لا يمكن إصدار فواتير الأقساط',
      },
    },
  ],
  projects: [
    {
      module: 'ai',
      kind: 'uses',
      reason: {
        en: 'Projects work as before, without the assistant (ask, status drafts, explanations, plan breakdown)',
        ar: 'تعمل المشاريع كما هي دون المساعد الذكي (الأسئلة ومسودات التقارير والشرح وتقسيم الخطة)',
      },
    },
    {
      module: 'tasks',
      kind: 'uses',
      reason: {
        en: 'Projects keep their plan, but nobody sees or adds project tasks, and task-based progress stops',
        ar: 'تبقى خطط المشاريع لكن لا يمكن عرض مهامها أو إضافتها، ويتوقف قياس الإنجاز بالمهام',
      },
    },
    {
      module: 'crm',
      kind: 'uses',
      reason: {
        en: 'Existing customers can be picked, but nobody can add or correct them',
        ar: 'يمكن اختيار العملاء الحاليين لكن لا يمكن إضافتهم أو تصحيح بياناتهم',
      },
    },
    {
      module: 'sales',
      kind: 'uses',
      reason: {
        en: 'Projects keep their figures, but billing waits and no quotation can start a project',
        ar: 'تحتفظ المشاريع بأرقامها لكن تتوقف الفوترة ولا يمكن بدء مشروع من عرض سعر',
      },
    },
    {
      module: 'purchasing',
      kind: 'uses',
      reason: {
        en: 'Projects keep their recorded costs, but no new purchases reach them',
        ar: 'تحتفظ المشاريع بتكاليفها المسجلة لكن لا تصلها مشتريات جديدة',
      },
    },
    {
      module: 'expenses',
      kind: 'uses',
      reason: {
        en: 'Projects keep their recorded costs, but no new expenses reach them',
        ar: 'تحتفظ المشاريع بتكاليفها المسجلة لكن لا تصلها مصروفات جديدة',
      },
    },
    {
      module: 'inventory',
      kind: 'uses',
      reason: {
        en: 'Materials can still be issued to projects, but nobody sees stock levels or movements',
        ar: 'يمكن صرف المواد للمشاريع لكن لا يمكن الاطلاع على أرصدة المخزون أو حركاته',
      },
    },
  ],
}

/**
 * The modules still on that rely on `module` (for the console's warning when it is turned off), with how.
 */
export function modulesRelyingOn(
  module: ModuleKey,
  enabled: readonly ModuleKey[],
): { module: ModuleKey; dependency: ModuleDependency }[] {
  return enabled.flatMap((m) =>
    m === module
      ? []
      : MODULE_DEPENDENCIES[m]
          .filter((d) => d.module === module)
          .map((dependency) => ({ module: m, dependency })),
  )
}

/** What a user may actually do: the permissions their roles grant, within the enabled modules. */
export function effectivePermissions(
  granted: Iterable<Permission>,
  modules: readonly ModuleKey[],
): Set<Permission> {
  const enabled = new Set<string>(modules)
  const out = new Set<Permission>()
  for (const p of granted) {
    const module = permissionModule(p)
    if (module === null || enabled.has(module)) out.add(p)
  }
  return out
}
